import { ContentType, DataObject } from "utils/types";

export const languageOptions = [
  { label: "English", value: "English" },
  { label: "French", value: "French" },
  { label: "Spanish", value: "Spanish" },
  { label: "Portuguese", value: "Portuguese" },
  { label: "German", value: "German" },
  { label: "Italian", value: "Italian" },
  { label: "Polish", value: "Polish" },
];

export const nicheOptions = [
  { label: "Recipe", value: "RECIPE" },
  { label: "Food", value: "FOOD" },
  { label: "Garden", value: "GARDEN" },
  { label: "Travel", value: "TRAVEL" },
  { label: "Home", value: "HOME" },
  { label: "Animals", value: "ANIMALS" },
  { label: "Tech", value: "TECH" },
  { label: "Gaming", value: "GAMING" },
  { label: "Sports", value: "SPORTS" },
  { label: "Motivation", value: "MOTIVATION" },
  { label: "Finance", value: "FINANCE" },
  { label: "Lifestyle", value: "LIFESTYLE" },
  { label: "Entertainment", value: "ENTERTAINMENT" },
  { label: "Adventure", value: "ADVENTURE" },
  { label: "Wellness", value: "WELLNESS" },
  { label: "Lifestyle", value: "LIFESTYLE" },
  { label: "Beauty", value: "BEAUTY" },
  { label: "Parenting", value: "PARENTING" },
  { label: "Other", value: "OTHER" },
];

export const outputTypePostOptions = [
  { label: "Generate All ( Content + Images )", value: "all" },
  { label: "Generate Content Only", value: "content" },
  { label: "Generate Images Only", value: "images" },
];

export const outputTypeArticleOptions = [
  { label: "Generate All ( Content + Images )", value: "all" },
  { label: "Generate Content Only", value: "content" },
];

export const articleLengthOptions = [
  { label: "+1K words", value: 1000 },
  { label: "+2k words", value: 2000 },
];

export const aspectRationOptions = [
  { label: "4:3", value: "4:3" },
  { label: "3:2", value: "3:2" },
  { label: "4:5", value: "4:5" },
  { label: "9:16", value: "9:16" },
  { label: "1:2", value: "1:2" },
  { label: "1:3", value: "1:3" },
  { label: "1:1", value: "1:1" },
];

export const articlePluginTargetOptions = [
  { label: "Yoast SEO", value: "Yoast" },
  { label: "RankMath", value: "RankMath" },
];

export const commonSettingsFields = [
  {
    name: "openAiToken",
    label: "OpenAI Token",
    type: "text",
    placeholder: "Enter Your OpenAI Token",
  },
  {
    name: "midjourneyToken",
    label: "Midjourney Token",
    type: "text",
    placeholder: "Enter your Midjourney Token",
  },
  {
    name: "applicationId",
    label: "Application Id",
    type: "text",
    placeholder: "Enter Your Application Id",
  },
  {
    name: "sessionId",
    label: "Session ID",
    type: "text",
    placeholder: "Enter Your Session ID",
  },
  {
    name: "serverId",
    label: "Server ID",
    type: "text",
    placeholder: "Enter Your Server ID",
  },
  {
    name: "channelId",
    label: "Channel ID",
    type: "text",
    placeholder: "Enter Your Channel ID",
  },
];

export const articleWebsiteFields = [
  {
    name: "websiteUrl",
    label: "Website URL",
    type: "text",
    placeholder: "Enter Website URL",
  },
  {
    name: "websiteUsername",
    label: "Website Email/Username",
    type: "text",
    placeholder: "Enter Website Email or Username",
  },
  {
    name: "websitePassword",
    label: "Website Password",
    type: "password",
    placeholder: "Enter Website Password",
  },
];

export interface Option {
  label: string;
  value: string | number;
}

export interface fieldsByConfig {
  [key: string]: Array<
    | {
        name: string;
        label: string;
        hasInfo?: boolean;
        info?: React.ReactNode;
        type: "number" | "text" | "password";
        placeholder: string;
      }
    | {
        name: string;
        label: string;
        hasInfo: boolean;
        info?: React.ReactNode;
        type: "select";
        options: Option[];
      }
    | { name: string; label: string; hasInfo: boolean; type: "checkbox" }
    | {
        name: string;
        label: string;
        hasInfo: boolean;
        info?: React.ReactNode;
        type: "edittext";
        placeholder: string;
      }
  >;
}

// Mapping for configuration prompts per type
export const promptsFieldsByConfig: fieldsByConfig = {
  post: [
    {
      name: "outputType",
      label: "Output Type",
      hasInfo: false,
      type: "select",
      options: outputTypePostOptions,
    },
    {
      name: "niche",
      label: "Niche",
      hasInfo: false,
      type: "select",
      options: nicheOptions,
    },
    {
      name: "numberOfVariations",
      label: "Number of Variations",
      hasInfo: false,
      type: "number",
      placeholder: "Enter number of variations",
    },
    {
      name: "language",
      label: "Language",
      hasInfo: false,
      type: "select",
      options: languageOptions,
    },
    {
      name: "openAiPrompt",
      label: "OpenAI Prompt",
      hasInfo: true,
      info: (
        <>
          Please see prompts examples in the{" "}
          <strong>Documentation Prompts </strong>
          section
        </>
      ),
      type: "edittext",
      placeholder: "Enter OpenAI Prompt",
    },
    {
      name: "midjourneyPrompt",
      label: "Midjourney Prompt",
      hasInfo: true,
      info: (
        <>
          You can use the following variables: <strong>{`{image_url}`}</strong>
          {", "}
          <strong>{`{recipe_title}`}</strong>
          {", "}
          <strong>{`{recipe_ingredients}`}</strong>
          {" and "}
          <strong>{`{content}`}</strong>
        </>
      ),
      type: "edittext",
      placeholder: "Enter Midjourney Prompt",
    },
  ],
  pin: [
    {
      name: "language",
      label: "Language",
      hasInfo: false,
      type: "select",
      options: languageOptions,
    },
    {
      name: "outputImageAspectRatio",
      label: "Output Image Aspect Ratio",
      hasInfo: false,
      type: "select",
      options: aspectRationOptions,
    },
    {
      name: "pinTitlePrompt",
      label: "Pin Title Prompt",
      hasInfo: true,
      info: (
        <>
          Please see prompts examples in the{" "}
          <strong>Documentation Prompts </strong>
          section
        </>
      ),
      type: "edittext",
      placeholder: "Enter Pin Title Prompt",
    },
    {
      name: "pinDescriptionPrompt",
      label: "Pin Description Prompt",
      hasInfo: true,
      info: (
        <>
          Please see prompts examples in the{" "}
          <strong>Documentation Prompts </strong>
          section
        </>
      ),
      type: "edittext",
      placeholder: "Enter Pin Description Prompt",
    },
    {
      name: "midjourneyPrompt",
      label: "Midjourney Prompt",
      hasInfo: true,
      info: (
        <>
          You can use the following variables: <strong>{`{image_url}`}</strong>
          {", "}
          <strong>{`{recipe_title}`}</strong>
          {" or "}
          <strong>{`{content}`}</strong>
        </>
      ),
      type: "edittext",
      placeholder: "Enter Midjourney Prompt",
    },
  ],
  article: [
    {
      name: "enableTastyPlugin",
      label: "Enable Tasty Plugin",
      hasInfo: false,
      type: "checkbox",
    },
    {
      name: "customPromptUsed",
      label: "Use my own Article prompt",
      hasInfo: false,
      type: "checkbox",
    },
    {
      name: "articleLength",
      label: "Article Length",
      hasInfo: false,
      type: "select",
      options: articleLengthOptions,
    },
    {
      name: "niche",
      label: "Niche",
      hasInfo: false,
      type: "select",
      options: nicheOptions,
    },
    {
      name: "targetPlugin",
      label: "Target plugin for SEO",
      hasInfo: false,
      type: "select",
      options: articlePluginTargetOptions,
    },
    {
      name: "outputType",
      label: "Output Type",
      hasInfo: false,
      type: "select",
      options: outputTypeArticleOptions,
    },
    {
      name: "language",
      label: "Language",
      hasInfo: false,
      type: "select",
      options: languageOptions,
    },
    {
      name: "midjourneyPrompt",
      label: "Midjourney Prompt",
      hasInfo: true,
      info: (
        <>
          You can use the following variables: <strong>{`{image_url}`}</strong>
          {", "}
          <strong>{`{recipe_title}`}</strong>
          {" or "}
          <strong>{`{focus_keyphrase}`}</strong>
          {", and "}
          <strong>{`{recipe_ingredients}`}</strong>
        </>
      ),

      type: "edittext",
      placeholder: "Enter Midjourney Prompt",
    },
    {
      name: "articlePrompt",
      label: "Article Prompt",
      hasInfo: true,
      info: (
        <>
          Must contain the following variables:{" "}
          <strong>{`{focus_keyphrase}`}</strong>
          {", "}
          <strong>{`{recipe_ingredients}`}</strong>
          {", "}
          <strong>{`{internal_links}`}</strong>
          {" and "}
          <strong>{`{external_link}`}</strong>
        </>
      ),
      type: "edittext",
      placeholder: "Enter Your Own Article Prompt",
    },
  ],
};

const getDefaultPrompts = (config: ContentType) => {
  const fields = promptsFieldsByConfig[config] || [];
  return fields.reduce(
    (acc, field) => {
      let val;
      if (field.type === "checkbox") {
        val = false;
      } else if (field.type === "number") {
        val = 1;
      } else if (field.type === "select") {
        // Use the first option's value if available
        val =
          field.options && field.options.length > 0
            ? field.options[0].value
            : "";
      } else {
        val = "";
      }
      return { ...acc, [field.name]: val };
    },
    {} as Record<string, any>
  );
};

export const configDataToFormValues = (data: any): DataObject => {
  const prompts: Record<string, any> = {
    language: data.language || "en",
    midjourneyPrompt: data.midjourneyPrompt || "",
  };

  if (data.type === "article") {
    Object.assign(prompts, {
      enableTastyPlugin: data.enableTastyPlugin || false,
      articleLength: data.articleLength || 1000,
      targetPlugin: data.targetPlugin || "Yoast",
      customPromptUsed: data.customPromptUsed || false,
      articlePrompt: data.articlePrompt || "",
    });
  }

  if (data.type === "post") {
    Object.assign(prompts, {
      openAiPrompt: data.openAiPrompt || "",
      numberOfVariations: data.numberOfVariations || 1,
    });
  }

  if (data.type === "pin") {
    Object.assign(prompts, {
      outputImageAspectRatio: data.outputImageAspectRatio || "4:3",
      pinTitlePrompt: data.pinTitlePrompt || "",
      pinDescriptionPrompt: data.pinDescriptionPrompt || "",
    });
  }

  if (["article", "post"].includes(data.type)) {
    Object.assign(prompts, {
      outputType: data.outputType || "all",
      niche: data.niche || "RECIPE",
    });
  }

  return {
    title: {
      ...defaultFormValues.title,
      [data.type]: data.title,
    },
    commonSettings: {
      openAiToken: data.openAiToken || "",
      midjourneyToken: data.midjourneyToken || "",
      applicationId: data.applicationId || "",
      sessionId: data.sessionId || "",
      serverId: data.serverId || "",
      channelId: data.channelId || "",
    },
    websiteSettings: {
      websiteUrl: data.websiteUrl || "",
      websiteUsername: data.websiteUsername || "",
      websitePassword: data.websitePassword || "",
    },
    prompts: {
      [data.type]: prompts,
    },
  };
};

const defaultPrompts = {
  post: getDefaultPrompts("post"),
  pin: getDefaultPrompts("pin"),
  article: getDefaultPrompts("article"),
};

export const defaultFormValues = {
  title: {
    post: "",
    pin: "",
    article: "",
  },
  commonSettings: {
    openAiToken: "",
    midjourneyToken: "",
    applicationId: "",
    sessionId: "",
    serverId: "",
    channelId: "",
  },
  websiteSettings: {
    websiteUrl: "",
    websiteUsername: "",
    websitePassword: "",
  },
  prompts: defaultPrompts,
};
